import React from "react";
import { withStyles } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/KeyboardArrowLeft";
import Trailer from "./Trailer";
import ShipmentDetail from "../ShipmentDetail";
import Grow from "@material-ui/core/Grow";
import { AppLoading } from "../../../../../loaders/loaders";
import { getDataWithQueryString } from "../../../../../api/componentAPIs";
import {
  getGateOutLoadingZoneDetails,
  getPlannedLoadingZoneDetails,
  sortByProType,
} from "./helper";
import { intersection, difference } from "lodash/array";
import constant from "./constant";

const styles = (theme) => ({
  container: {
    boxSizing: "border-box",
    color: theme.TextColor,
    height: "100%",
    "& *": {
      boxSizing: "border-box",
    },
  },
  header: {
    color: theme.TextColor,
    display: "flex",
    padding: "4px 0",
    borderBottom: `1px solid ${theme.AppBGColor}`,
    fontSize: "14px",
    textAlign: "center",
    lineHeight: "21px",
    fontWeight: "bold",
  },
  backButton: {
    color: theme.gridTextColor,
    cursor: "pointer",
  },
  title: {
    color: theme.palette.primary.main,
  },
  content: {
    display: "flex",
    textAlign: "center",
    flexWrap: "wrap",
    overflowX: "hidden",
    overflowY: "auto",
    cursor: "pointer",
    justifyContent: "center",
    flexGrow: 1,
  },
  centerContent: {},
  trailerContainer: {
    height: "100%",
    padding: "20px 30px 0 30px",
    display: "flex",
    minHeight: 800,
  },
  contentContainer: {
    display: "flex",
    height: "calc(100% - 40px)",
    overflow: "hidden",
  },
  prosContainer: {
    overflowY: "auto",
    overflowX: "hidden",
    padding: 16,
    flexGrow: 1,
    "&$noData": {
      minWidth: "50%",
    },
  },
  layoutTitle: {
    color: theme.HeaderSelectedText,
    flex: 1,
  },
  backIcon: {
    verticalAlign: "middle",
    fontSize: "25px",
  },
  proShown: {
    justifyContent: "space-between",
  },
  noData: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.TextColor,
  },
});

class TrailerLayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      plannedTrailerData: null,
      dispatchedTrailerData: null,
      zoneSelected: 0,
      activeProSection: null,
      filter: {
        loadingZone: null,
        proType: null,
        routeType: null,
      },
      proDetails: [],
      filteredProList: [],
    };
    this.dataLoaded = false;
    this._isComponentMounted = false;
    this.getComplianceShipments();
  }

  componentDidMount() {
    this._isComponentMounted = true;
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
  }

  getComplianceShipments = () => {
    const {
      selectedPlannedRoute,
      planDate,
      terminalCode,
      manifestNumber,
    } = this.props;
    let queryString = "";
    queryString = selectedPlannedRoute
      ? `&plannedRoute=${selectedPlannedRoute}`
      : "";
    queryString += manifestNumber ? `&manifestNumber=${manifestNumber}` : "";
    getDataWithQueryString(
      "GetShipmentDetails",
      `planDate=${planDate}&terminalCode=${terminalCode}${queryString}`
    ).then(
      (response) => {
        if (this._isComponentMounted) {
          this.dataLoaded = true;
          if (response.status === 200) {
            this.setState({
              plannedTrailerData: this.getPlannedTrailerData(response.data),
              dispatchedTrailerData: this.getDispatchedTrailerData(
                response.data
              ),
              proDetails: response.data.proDetails,
            });
          } else {
            this.setState({
              plannedTrailerData: null,
              dispatchedTrailerData: null,
            });
          }
        }
      },
      (error) => {
        if (this._isComponentMounted) {
          this.dataLoaded = true;
          this.setState({
            plannedTrailerData: null,
            dispatchedTrailerData: null,
          });
        }
      }
    );
  };

  updateFilter = ({
    loadingZone = null,
    proType = null,
    routeType = null,
  } = {}) => {
    this.setState(
      {
        filter: {
          loadingZone,
          proType,
          routeType,
        },
      },
      this.filterCardData
    );
  };

  matchesLoadingZone = (proId, loadingZone) => {
    loadingZone = loadingZone === constant.ZONES.NOFMRT ? null : loadingZone;
    const { plannedTrailerData, dispatchedTrailerData } = this.state;
    return (
      plannedTrailerData.proDetail.some(
        (pro) => pro.proId === proId && pro.loadingZone === loadingZone
      ) ||
      dispatchedTrailerData.proDetail.some(
        (pro) => pro.proId === proId && pro.loadingZone === loadingZone
      )
    );
  };

  matchesProType = (proId, proType, loadingZone) => {
    const { plannedTrailerData, dispatchedTrailerData } = this.state;

    const [plannedZoneBills, gateoutZoneBills] = loadingZone
      ? [
          plannedTrailerData.proDetail.filter(
            (bill) => bill.loadingZone === loadingZone
          ),
          dispatchedTrailerData.proDetail.filter(
            (bill) => bill.loadingZone === loadingZone
          ),
        ]
      : [plannedTrailerData.proDetail, dispatchedTrailerData.proDetail];

    switch (proType) {
      case constant.PRO_TYPE.MISSED:
        return (
          plannedZoneBills.some((pro) => pro.proId === proId) &&
          !gateoutZoneBills.some((pro) => pro.proId === proId)
        );
      case constant.PRO_TYPE.EXTRA:
        return (
          !plannedZoneBills.some((pro) => pro.proId === proId) &&
          gateoutZoneBills.some((pro) => pro.proId === proId)
        );
      case constant.PRO_TYPE.MATCHED:
        return (
          plannedZoneBills.some((pro) => pro.proId === proId) &&
          gateoutZoneBills.some((pro) => pro.proId === proId)
        );
      default:
        break;
    }
  };

  filterCardData = () => {
    const { plannedTrailerData, dispatchedTrailerData, filter } = this.state;
    const { loadingZone, proType, routeType } = filter;
    let proIdList = [];
    if (routeType) {
      const routeProList =
        routeType === constant.ROUTE_TYPE.ACTUAL
          ? dispatchedTrailerData.proDetail
          : plannedTrailerData.proDetail;
      proIdList = routeProList.map((pro) => pro.proId);
    } else {
      proIdList = [
        ...new Set(
          [
            ...plannedTrailerData.proDetail,
            ...dispatchedTrailerData.proDetail,
          ].map((pro) => pro.proId)
        ),
      ];
    }

    const filteredProList = [];
    if (routeType || proType || loadingZone) {
      this.state.proDetails.forEach((pro) => {
        let filterStatus = true;
        if (routeType) {
          if (proType === constant.PRO_TYPE.MISSED) {
            filterStatus = !proIdList.includes(pro.proId);
          } else {
            filterStatus = proIdList.includes(pro.proId);
          }
        }
        if (loadingZone) {
          filterStatus =
            filterStatus && this.matchesLoadingZone(pro.proId, loadingZone);
        }
        if (proType) {
          filterStatus =
            filterStatus &&
            this.matchesProType(pro.proId, proType, loadingZone);
        }

        const plannedProDetail = plannedTrailerData.proDetail.find(
          (plannedPro) => pro.proId === plannedPro.proId
        );
        pro.plannedRoute = plannedProDetail
          ? plannedTrailerData.routeName
          : null;
        pro.plannedLoadingZone = plannedProDetail
          ? plannedProDetail.loadingZone
          : null;

        const gateoutProDetail = dispatchedTrailerData.proDetail.find(
          (plannedPro) => pro.proId === plannedPro.proId
        );
        pro.gateoutRoute = gateoutProDetail
          ? dispatchedTrailerData.routeName
          : null;
        pro.gateoutLoadingZone = gateoutProDetail
          ? gateoutProDetail.loadingZone
          : null;

        let billType = null;
        if (loadingZone === constant.ZONES.NOFMRT) {
          billType = constant.ZONES.NOFMRT;
        } else if (routeType) {
          billType =
            pro.plannedRoute && pro.gateoutRoute
              ? constant.PRO_TYPE.MATCHED
              : billType;
          billType =
            !billType && !pro.plannedRoute && pro.gateoutRoute
              ? constant.PRO_TYPE.EXTRA
              : billType;
          billType =
            !billType && !pro.gateoutRoute && pro.plannedRoute
              ? constant.PRO_TYPE.MISSED
              : billType;
        } else {
          billType =
            pro.plannedLoadingZone === pro.gateoutLoadingZone
              ? constant.PRO_TYPE.MATCHED
              : billType;
          billType =
            !billType && pro.plannedLoadingZone !== loadingZone
              ? constant.PRO_TYPE.EXTRA
              : billType;
          billType =
            !billType && pro.gateoutLoadingZone !== loadingZone
              ? constant.PRO_TYPE.MISSED
              : billType;
        }
        pro.billType = billType;
        console.log(
          pro.proId,
          billType,
          routeType,
          pro.plannedRoute,
          pro.gateoutRoute
        );
        if (filterStatus) {
          filteredProList.push(pro);
        }
      });
    }

    this.setState({
      filteredProList: filteredProList.sort(sortByProType),
      showPros: Boolean(routeType || proType || loadingZone),
    });
  };

  getPlannedTrailerData = (trailerData) => {
    const { plannedRoute } = trailerData;
    const data = {
      ...plannedRoute,
      zoneDetails: {
        front: getPlannedLoadingZoneDetails(
          plannedRoute.proDetail,
          constant.ZONES.FRONT
        ),
        middle: getPlannedLoadingZoneDetails(
          plannedRoute.proDetail,
          constant.ZONES.MIDDLE
        ),
        rear: getPlannedLoadingZoneDetails(
          plannedRoute.proDetail,
          constant.ZONES.REAR
        ),
        tail: getPlannedLoadingZoneDetails(
          plannedRoute.proDetail,
          constant.ZONES.TAIL
        ),
        noFMRT: getPlannedLoadingZoneDetails(
          plannedRoute.proDetail,
          constant.ZONES.NOFMRT
        ),
      },
      details: {
        totalBills: plannedRoute.proDetail.length,
        missedBills: null,
        extraBills: null,
        matchedBills: null,
      },
    };
    return data;
  };

  getDispatchedTrailerData = (trailerData) => {
    const { plannedRoute, dispatchedRoute } = trailerData;
    const plannedProList = plannedRoute.proDetail.map((pro) => pro.proId);
    const dispatchedProList = dispatchedRoute.proDetail.map((pro) => pro.proId);

    const data = {
      ...dispatchedRoute,
      zoneDetails: {
        front: getGateOutLoadingZoneDetails({
          plannedBills: plannedRoute.proDetail,
          gateoutBills: dispatchedRoute.proDetail,
          zone: constant.ZONES.FRONT,
        }),
        middle: getGateOutLoadingZoneDetails({
          plannedBills: plannedRoute.proDetail,
          gateoutBills: dispatchedRoute.proDetail,
          zone: constant.ZONES.MIDDLE,
        }),
        rear: getGateOutLoadingZoneDetails({
          plannedBills: plannedRoute.proDetail,
          gateoutBills: dispatchedRoute.proDetail,
          zone: constant.ZONES.REAR,
        }),
        tail: getGateOutLoadingZoneDetails({
          plannedBills: plannedRoute.proDetail,
          gateoutBills: dispatchedRoute.proDetail,
          zone: constant.ZONES.TAIL,
        }),
        noFMRT: getGateOutLoadingZoneDetails({
          plannedBills: plannedRoute.proDetail,
          gateoutBills: dispatchedRoute.proDetail,
          zone: null,
        }),
      },
      details: {
        totalBills: dispatchedRoute.proDetail.length,
        missedBills: difference(plannedProList, dispatchedProList).length,
        extraBills: difference(dispatchedProList, plannedProList).length,
        matchedBills: intersection(plannedProList, dispatchedProList).length,
      },
    };
    return data;
  };

  renderTrailers = () => {
    const { classes } = this.props;
    const {
      plannedTrailerData,
      dispatchedTrailerData,
      zoneSelected,
      activeProSection,
    } = this.state;
    return (
      <div
        className={`${classes.content} ${
          zoneSelected ? "" : classes.centerContent
        } customScroll`}
        onClick={this.updateFilter}
      >
        <div className={classes.trailerContainer}>
          <Grow in={true} timeout={1000}>
            <Trailer
              routeType={constant.ROUTE_TYPE.PLANNED}
              billType={constant.BILL_TYPE.PLANNED}
              trailerData={plannedTrailerData}
              filter={this.state.filter}
              updateFilter={this.updateFilter}
            />
          </Grow>
        </div>

        <div className={classes.trailerContainer}>
          <Grow in={true} timeout={2000}>
            <Trailer
              routeType={constant.ROUTE_TYPE.ACTUAL}
              billType={constant.BILL_TYPE.ACTUAL}
              trailerData={dispatchedTrailerData}
              filter={this.state.filter}
              updateFilter={this.updateFilter}
            />
          </Grow>
        </div>
      </div>
    );
  };

  trailerRenderer = () => {
    const { classes } = this.props;
    const { plannedTrailerData, dispatchedTrailerData } = this.state;
    if (!this.dataLoaded) {
      return <AppLoading />;
    }

    if (!plannedTrailerData && !dispatchedTrailerData) {
      return <div className={classes.noData}> No data available</div>;
    }

    return this.renderTrailers();
  };

  render() {
    const { classes } = this.props;
    const { dispatchedTrailerData, filteredProList, showPros } = this.state;
    return (
      <div className={classes.container}>
        <header className={classes.header}>
          <div className={classes.backButton} onClick={this.props.goBack}>
            <ArrowBackIcon classes={{ root: classes.backIcon }} /> Back
          </div>
          <div className={classes.layoutTitle}>
            Shipments On the Trailer:{" "}
            {dispatchedTrailerData ? dispatchedTrailerData.trailerNumber : ""}
          </div>
        </header>
        <div
          className={`${classes.contentContainer} ${
            showPros ? classes.proShown : ""
          }`}
        >
          {this.trailerRenderer()}

          {showPros && (
            <div
              className={`${classes.prosContainer} ${
                filteredProList.length ? "" : classes.noData
              } customScroll`}
            >
              {filteredProList.length ? (
                filteredProList.map((pro) => (
                  <ShipmentDetail
                    data={pro}
                    key={pro.proId}
                    billType={pro.billType}
                  />
                ))
              ) : (
                <div>No Bills</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(TrailerLayout);
